/**
 * Minimal debounce helper. Wraps a callback so it only runs once calls have
 * stopped arriving for `waitMs` milliseconds.
 *
 * Used by the panel's server-side search boxes (driver search, taxi search,
 * SMS monitor) so typing fires one request per pause rather than one per
 * keystroke. The `:search` prop on the other tables filters rows already in
 * memory and needs none of this.
 *
 * Hand-rolled on purpose: the panel has no lodash, and this is the only place
 * that needs debouncing, so a new dependency is not worth it.
 *
 * Callbacks take no arguments — every caller reads the current query straight
 * off the component, so there is nothing to forward through the timer.
 */
export interface DebouncedFunction {
    (): void,
    cancel(): void
}

export default function debounce(fn: () => void, waitMs: number): DebouncedFunction {
    let timer: number | undefined;

    const debounced = (() => {
        if (timer !== undefined) {
            window.clearTimeout(timer);
        }
        timer = window.setTimeout(() => {
            timer = undefined;
            fn();
        }, waitMs);
    }) as DebouncedFunction;

    /**
     * Drops a pending call. Needed when the query falls back below the
     * minimum length: without it, an already-scheduled search would still
     * fire against the too-short query a moment later.
     */
    debounced.cancel = () => {
        if (timer !== undefined) {
            window.clearTimeout(timer);
            timer = undefined;
        }
    };

    return debounced;
}
